import { UTCDate } from '@date-fns/utc';
import { Typography, styled } from '@mui/material';
import {
    addDays,
    getDate,
    getISOWeek,
    lastDayOfWeek,
    startOfWeek,
} from 'date-fns';
import Tooltip from 'components/interface/tooltip/Tooltip';
import React from 'react';
import { getDayPresentation, RangeTone } from './calendarMonthPresentation';
import { calendarLayout } from './constants';
import { CalendarDateRange, DateRangePickerVariant } from './types';
import { normalizeDate } from './utils';

const Base = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(calendarLayout.compactGap),
    width: 'fit-content',
}));

const HeaderRow = styled('div')(() => ({
    display: 'flex',
    alignItems: 'stretch',
}));

const WeekHeaderSpacer = styled('div')(() => ({
    width: calendarLayout.weekNumberWidth,
    flexShrink: 0,
}));

const ColumnGap = styled('div')(({ theme }) => ({
    width: theme.spacing(calendarLayout.compactGap),
    flexShrink: 0,
}));

const HeaderDays = styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: `repeat(7, ${calendarLayout.dayCellSize}px)`,
}));

const HeaderCell = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: calendarLayout.weekdayHeaderHeight,
    userSelect: 'none',
    color: theme.palette.text.secondary,
}));

const Weeks = styled('div')(({ theme }) => ({
    'position': 'relative',
    'display': 'flex',
    'flexDirection': 'column',
    'gap': theme.spacing(calendarLayout.compactGap),
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: calendarLayout.weekNumberWidth,
        backgroundColor: theme.palette.grey[200],
        borderRadius: theme.shape.borderRadius,
    },
}));

const WeekRow = styled('div')(() => ({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'stretch',
}));

const WeekNumber = styled('div')(({ theme }) => ({
    ...theme.typography.caption,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: calendarLayout.dayCellSize,
    width: calendarLayout.weekNumberWidth,
    flexShrink: 0,
    color: theme.palette.text.primary,
    fontWeight: 700,
    userSelect: 'none',
}));

const DaysRow = styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: `repeat(7, ${calendarLayout.dayCellSize}px)`,
}));

const DayCell = styled('div', {
    shouldForwardProp: prop =>
        ![
            '$tone',
            '$isSegmentStart',
            '$isSegmentEnd',
            '$customBackground',
        ].includes(prop as string),
})<{
    $tone: RangeTone;
    $isSegmentStart: boolean;
    $isSegmentEnd: boolean;
    $customBackground?: string;
}>(({ theme, $tone, $isSegmentStart, $isSegmentEnd, $customBackground }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: calendarLayout.dayCellSize,
    height: calendarLayout.dayCellSize,
    background:
        $tone === 'selected' || $tone === 'preview'
            ? $customBackground ||
              ($tone === 'selected'
                  ? theme.palette.primary[50]
                  : theme.palette.primary[25])
            : 'transparent',
    borderTopLeftRadius: $isSegmentStart ? theme.shape.borderRadius : 0,
    borderBottomLeftRadius: $isSegmentStart ? theme.shape.borderRadius : 0,
    borderTopRightRadius: $isSegmentEnd ? theme.shape.borderRadius : 0,
    borderBottomRightRadius: $isSegmentEnd ? theme.shape.borderRadius : 0,
}));

const DayButton = styled('button', {
    shouldForwardProp: prop =>
        ![
            '$tone',
            '$isEndpoint',
            '$isToday',
            '$isMainMonth',
            '$disabled',
            '$readOnly',
            '$customEndpointColor',
            '$customRangeColor',
            '$customTextColor',
        ].includes(prop as string),
})<{
    $tone: RangeTone;
    $isEndpoint: boolean;
    $isToday: boolean;
    $isMainMonth: boolean;
    $disabled: boolean;
    $readOnly: boolean;
    $customEndpointColor?: string;
    $customRangeColor?: string;
    $customTextColor?: string;
}>(
    ({
        theme,
        $tone,
        $isEndpoint,
        $isToday,
        $isMainMonth,
        $disabled,
        $readOnly,
        $customEndpointColor,
        $customRangeColor,
        $customTextColor,
    }) => ({
        ...theme.typography.caption,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: calendarLayout.dayCellSize,
        height: calendarLayout.dayCellSize,
        margin: 0,
        padding: 0,
        border: 'none',
        borderRadius: theme.shape.borderRadius,
        background:
            !$tone || !$isEndpoint
                ? 'transparent'
                : $customEndpointColor ||
                  ($tone === 'selected'
                      ? theme.palette.primary[200]
                      : theme.palette.primary[100]),
        color:
            $customTextColor ||
            ($isMainMonth
                ? theme.palette.text.primary
                : theme.palette.text.secondary),
        opacity: $disabled ? 0.4 : 1,
        cursor: $disabled || $readOnly ? 'default' : 'pointer',
        userSelect: 'none',
        transition: theme.transitions.create([
            'background-color',
            'box-shadow',
        ]),
        ...($isToday && {
            boxShadow: `inset 0 0 0 1px ${theme.palette.grey[500]}`,
        }),
        ...(!$disabled &&
            !$readOnly && {
                '&:hover': {
                    background: !$tone
                        ? theme.palette.grey[200]
                        : $isEndpoint
                          ? $customEndpointColor ||
                            ($tone === 'selected'
                                ? theme.palette.primary[300]
                                : theme.palette.primary[200])
                          : $customRangeColor ||
                            ($tone === 'selected'
                                ? theme.palette.primary[100]
                                : theme.palette.primary[50]),
                },
            }),
    })
);

interface CalendarMonthProps {
    year: number;
    month: number;
    variant?: DateRangePickerVariant;
    periods: CalendarDateRange[];
    previewPeriod?: CalendarDateRange | null;
    disableBefore?: UTCDate;
    disableAfter?: UTCDate;
    disabledDates?: UTCDate[];
    readOnly?: boolean;
    onDayClick: (day: UTCDate) => void;
    onDayHover?: (day: UTCDate | null) => void;
}

interface Week {
    weekNumber: number;
    days: UTCDate[];
}

const weekDayLabels = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];

function buildWeeks(year: number, month: number): Week[] {
    let cursor = startOfWeek(new UTCDate(year, month, 1), {
        weekStartsOn: 1,
    });
    const lastDay = lastDayOfWeek(new UTCDate(year, month + 1, 0), {
        weekStartsOn: 1,
    });
    const weeks: Week[] = [];

    while (cursor <= lastDay) {
        const days: UTCDate[] = [];
        for (let index = 0; index < 7; index++) {
            days.push(cursor);
            cursor = addDays(cursor, 1);
        }

        weeks.push({
            weekNumber: getISOWeek(days[0]),
            days,
        });
    }

    return weeks;
}

export const CalendarMonth = ({
    year,
    month,
    variant = 'range',
    periods,
    previewPeriod,
    disableBefore,
    disableAfter,
    disabledDates,
    readOnly = false,
    onDayClick,
    onDayHover,
}: CalendarMonthProps) => {
    const weeks = React.useMemo(() => buildWeeks(year, month), [year, month]);
    const today = React.useMemo(() => {
        // Only "today" depends on the user's local date; calendar arithmetic stays in UTC.
        const now = new Date();
        return new UTCDate(now.getFullYear(), now.getMonth(), now.getDate());
    }, []);
    const disabledDateKeys = React.useMemo(
        () =>
            new Set(
                (disabledDates || []).map(date => normalizeDate(date).getTime())
            ),
        [disabledDates]
    );
    const dayPresentations = React.useMemo(() => {
        const presentations: Record<
            number,
            ReturnType<typeof getDayPresentation>
        > = {};

        for (const week of weeks) {
            for (const day of week.days) {
                presentations[day.getTime()] = getDayPresentation({
                    day,
                    month,
                    variant,
                    periods,
                    previewPeriod,
                    disableBefore,
                    disableAfter,
                    disabledDateKeys,
                    today,
                });
            }
        }

        return presentations;
    }, [
        disableAfter,
        disableBefore,
        disabledDateKeys,
        month,
        variant,
        periods,
        previewPeriod,
        today,
        weeks,
    ]);

    return (
        <Base onMouseLeave={() => !readOnly && onDayHover?.(null)}>
            <HeaderRow>
                <WeekHeaderSpacer />
                <ColumnGap />
                <HeaderDays>
                    {weekDayLabels.map((label, index) => (
                        <HeaderCell key={`${label}-${index}`}>
                            <Typography variant='caption' fontWeight={500}>
                                {label}
                            </Typography>
                        </HeaderCell>
                    ))}
                </HeaderDays>
            </HeaderRow>
            <Weeks>
                {weeks.map(week => (
                    <WeekRow
                        key={`${week.weekNumber}-${week.days[0].toISOString()}`}>
                        <WeekNumber>{week.weekNumber}</WeekNumber>
                        <ColumnGap />
                        <DaysRow>
                            {week.days.map(day => {
                                const presentation =
                                    dayPresentations[day.getTime()];

                                const dayButton = (
                                    <DayButton
                                        type='button'
                                        $tone={presentation.tone}
                                        $isEndpoint={presentation.isEndpoint}
                                        $isToday={presentation.isToday}
                                        $isMainMonth={presentation.isMainMonth}
                                        $disabled={presentation.isDisabled}
                                        $readOnly={readOnly}
                                        $customEndpointColor={
                                            presentation.range?.color?.endpoint
                                        }
                                        $customRangeColor={
                                            presentation.range?.color
                                                ?.background
                                        }
                                        $customTextColor={
                                            presentation.range?.color?.text
                                        }
                                        onClick={() => {
                                            if (
                                                !presentation.isDisabled &&
                                                !readOnly
                                            ) {
                                                onDayClick(day);
                                            }
                                        }}
                                        onMouseEnter={() => {
                                            if (
                                                !presentation.isDisabled &&
                                                !readOnly
                                            ) {
                                                onDayHover?.(day);
                                            }
                                        }}>
                                        {getDate(day)}
                                    </DayButton>
                                );

                                return (
                                    <DayCell
                                        key={day.toISOString()}
                                        $tone={presentation.tone}
                                        $isSegmentStart={
                                            presentation.isSegmentStart
                                        }
                                        $isSegmentEnd={
                                            presentation.isSegmentEnd
                                        }
                                        $customBackground={
                                            presentation.range?.color
                                                ?.background
                                        }>
                                        {presentation.range?.tooltip ? (
                                            <Tooltip
                                                title={
                                                    presentation.range.tooltip
                                                }>
                                                {dayButton}
                                            </Tooltip>
                                        ) : (
                                            dayButton
                                        )}
                                    </DayCell>
                                );
                            })}
                        </DaysRow>
                    </WeekRow>
                ))}
            </Weeks>
        </Base>
    );
};

export default CalendarMonth;
